import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Paper,
  Box,
  Tooltip,
  Typography,
  Switch,
  FormControlLabel,
  Divider,
  LinearProgress,
  IconButton,
  Chip,
} from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import {
  clear_notification,
  delete_notification,
} from "redux/actions/AppActions";
import {
  addTask,
  clearTasks,
  deleteTask,
  markTaskAsRead,
  updateTask,
} from "redux/actions/BackgroundTaskActions";
import NotificationItem from "./NotificationItem";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import { epochConverter } from "config/helpers/date_helper";
import Confirmation from "components/ui/ConfirmationDialog/ConfirmationDialog";
import { set_show_popup } from "redux/actions/ScribeAcitons";
import { extractPatientAndEncounterIdFromComposition } from "config/helpers/utils";
import { encounterObject } from "redux/actions/EncounterActions";
import { getCurrentRecordingState } from "config/helpers/recordingStateSelector";
import { getCurrentProgressState } from "config/helpers/progressStateSelector";
import { startWatchingTask } from "redux/redux-thunk/BackgroundtaskThunks";
import { TASK_STATUS } from "config/constants/constants";
import { GENERATE_REGENRATE_TEMPLATE } from "adapters/recognizer.service";
import { toast } from "react-toastify";
import { getUnixMs } from "config/helpers/date_helper";
import generateLabel from "../../config/helpers/syncLabel";
import { useActiveSyncTasks } from "../../hooks/useGlobalSync";
import { ClearAllRounded } from "@mui/icons-material";
import { mappedLanguages } from "config/helpers/common_helper";
import ProgressBar from "components/loader/LinearProgress";
import UITooltip from "components/ui/Tooltip/UITooltip";

const groupNotificationsByDate = (notifications, t) => {
  const groups = {
    [t("notifications.TODAY")]: [],
    [t("notifications.YESTERDAY")]: [],
    [t("notifications.LAST_7_DAYS")]: [],
    [t("notifications.OLDER")]: [],
  };

  const today = dayjs().startOf("day");
  const yesterday = today.subtract(1, "day");
  const sevenDaysAgo = today.subtract(7, "day");

  notifications.forEach((notification) => {
    const notificationDate = dayjs(notification.addedOn);
    if (notificationDate.isSame(today, "day")) {
      groups[t("notifications.TODAY")].push(notification);
    } else if (notificationDate.isSame(yesterday, "day")) {
      groups[t("notifications.YESTERDAY")].push(notification);
    } else if (notificationDate.isAfter(sevenDaysAgo)) {
      groups[t("notifications.LAST_7_DAYS")].push(notification);
    } else {
      groups[t("notifications.OLDER")].push(notification);
    }
  });

  for (const key in groups) {
    if (groups[key].length === 0) {
      delete groups[key];
    }
  }

  return groups;
};

const formatNotificationTimestamp = (timestamp) => {
  const now = dayjs();
  const notificationDate = dayjs(timestamp);

  if (notificationDate.isSame(now, "day")) {
    const diffMins = now.diff(notificationDate, "minute");
    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    const diffHours = now.diff(notificationDate, "hour");
    return `${diffHours}h ago`;
  }
  return notificationDate.format("MMM D");
};

const NotificationModal = ({ open, onClose }) => {
  const app = useSelector((state) => state?.app);
  const notificationMessage = app?.notification;
  const reduxDispatch = useDispatch();

  // Use hook to get active tasks instead of manual polling
  const activeTasksData = useActiveSyncTasks();

  // Keep single latest sync-complete notification
  const notifications = useMemo(() => {
    const raw = Array.isArray(notificationMessage) ? notificationMessage : [];
    const syncComplete = raw.filter((n) => n?.type === "sync-complete");
    if (syncComplete.length <= 1) return raw;
    const latest = syncComplete.reduce((a, b) =>
      (a?.addedOn || 0) > (b?.addedOn || 0) ? a : b,
    );
    const others = raw.filter((n) => n?.type !== "sync-complete");
    return [...others, latest];
  }, [notificationMessage]);

  // Merge notifications + poller data
  const { activeSyncGroup, completedSyncGroups, otherNotifications } =
    useMemo(() => {
      const syncTypes = new Set(["sync-progress", "sync-complete"]);
      const syncNotifications = (notifications || []).filter((n) =>
        syncTypes.has(n?.type),
      );
      const otherNotificationsLocal = (notifications || []).filter(
        (n) => !syncTypes.has(n?.type),
      );
      const grouped = {};

      syncNotifications.forEach((n) => {
        const id = n?.id || n?.message || "__unknown__";
        grouped[id] = { ...(grouped[id] || {}), ...n };
        if (grouped[id]?.type === "sync-complete") {
          grouped[id].label = grouped[id].label || "Task Sync";
          grouped[id].status = grouped[id].status || "COMPLETED";
          grouped[id].progress =
            typeof grouped[id].progress === "number"
              ? grouped[id].progress
              : 100;
          if (!grouped[id].addedOn) grouped[id].addedOn = Date.now();
        } else {
          grouped[id].label = grouped[id].label || null;
        }
      });

      (activeTasksData || []).forEach((t) => {
        const id = `${t.id}-sync`;
        const friendly = t.label ? generateLabel(t.label) : null;
        if (!grouped[id]) {
          grouped[id] = {
            id,
            progress: typeof t.progress === "number" ? t.progress : 0,
            status: t.status || "RUNNING",
            addedOn: Date.now(),
            type: "sync-progress",
            label: friendly,
            processedGroups: Array.isArray(t.processedGroups)
              ? t.processedGroups
              : null,
            subtaskCode: t.subtaskCode || null,
            subtaskProgress:
              typeof t.subtaskProgress === "number" ? t.subtaskProgress : null,
          };
        } else {
          if (friendly) grouped[id].label = friendly;
          grouped[id].progress =
            typeof t.progress === "number"
              ? t.progress
              : grouped[id].progress || 0;
          grouped[id].status = t.status || grouped[id].status || "RUNNING";
          if (t.subtaskCode) grouped[id].subtaskCode = t.subtaskCode;
          if (typeof t.subtaskProgress === "number")
            grouped[id].subtaskProgress = t.subtaskProgress;
          if (t.processedGroups) {
            grouped[id].processedGroups = Array.isArray(t.processedGroups)
              ? t.processedGroups
              : [];
          }
        }
      });

      const groups = Object.keys(grouped).map((id) => ({
        id,
        ...grouped[id],
      }));

      // Separate active (running) and completed sync groups
      const active =
        groups.find(
          (g) =>
            (g.status || "").toUpperCase() === "RUNNING" ||
            (g.progress || 0) < 100,
        ) || null;

      const completed = groups.filter(
        (g) =>
          (g.status || "").toUpperCase() === "COMPLETED" ||
          (g.progress || 0) === 100,
      );

      // Find the current active group from processedGroups if available
      let activeGroup = null;
      if (active && Array.isArray(active.processedGroups)) {
        const pgIndex = active.processedGroups.findIndex(
          (pg) => (pg.progress || 0) < 100,
        );
        activeGroup =
          pgIndex >= 0
            ? active.processedGroups[pgIndex]
            : active.processedGroups[0] || null;
      }

      return {
        activeSyncGroup: activeGroup,
        hasRunningSync: active !== null,
        completedSyncGroups: completed,
        otherNotifications: otherNotificationsLocal,
      };
    }, [notifications, activeTasksData]);

  const { i18n, t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [showUnreadOnly, setShowUnreadOnly] = useState(false);
  const [openClearConfirmation, setOpenClearConfirmation] = useState(false);
  const currentPractitionerId = useSelector(
    (state) => state.auth.practitionerData?.id,
  );
  const patientObj = useSelector((state) => state?.patientObj);
  const encounterObj = useSelector((state) => state?.encounterObj);
  const { recording: isRecording, paused: isPaused } = useSelector((state) =>
    getCurrentRecordingState(
      state,
      patientObj?.patientObj?.id,
      encounterObj?.encounter?.id,
    ),
  );
  const regularNotifications = useSelector(
    (state) => state.app?.notification || [],
  );
  const backgroundTasks = useSelector(
    (state) => state.backgroundTasks?.tasks || [],
  );
  const isDarkTheme = useSelector((state) => state.app.theme) === "dark";
  const progressState = useSelector((state) =>
    getCurrentProgressState(
      state,
      patientObj?.patientObj?.id,
      encounterObj?.encounter?.id,
    ),
  );
  const { isProgressReady } = progressState || {};

  const getNotificationPriority = (notification) => {
    const RECENT_THRESHOLD_MS = 15000;
    const now = Date.now();

    if (
      notification.type === "background_task" &&
      ["COMPLETED", "FAILED"].includes(notification.status) &&
      now - notification.lastUpdated < RECENT_THRESHOLD_MS
    ) {
      return 1;
    }

    return 2;
  };

  const allNotifications = useMemo(() => {
    const validRegular = regularNotifications.filter(
      (n) => n?.message?.trim() && n?.addedOn,
    );
    const validBackground = backgroundTasks.filter(
      (t) =>
        t?.message?.trim() &&
        t?.addedOn &&
        t.practitionerId === currentPractitionerId,
    );

    const combined = [...validRegular, ...validBackground];

    // Filter out sync notifications from the general list
    const filteredCombined = combined.filter(
      (n) => !["sync-progress", "sync-complete"].includes(n.type),
    );

    filteredCombined.sort((a, b) => {
      const priorityA = getNotificationPriority(a);
      const priorityB = getNotificationPriority(b);

      if (priorityA !== priorityB) {
        return priorityA - priorityB;
      }

      return b.addedOn - a.addedOn;
    });

    return filteredCombined;
  }, [regularNotifications, backgroundTasks, currentPractitionerId]);

  const groupedNotifications = useMemo(() => {
    const filtered = showUnreadOnly
      ? allNotifications.filter((n) => !n.isRead)
      : allNotifications;
    return groupNotificationsByDate(filtered, t);
  }, [allNotifications, showUnreadOnly, t]);

  const hasUnread = useMemo(
    () =>
      allNotifications.some(
        (n) =>
          !n.isRead &&
          !(
            n.type === "background_task" &&
            ["IN_PROGRESS", "QUEUED"].includes(n.status)
          ),
      ),
    [allNotifications],
  );

  const groupKeys = Object.keys(groupedNotifications);

  const performClear = () => {
    dispatch(clear_notification());
    dispatch(clearTasks());
  };

  const handleClearAllNotifications = () => {
    reduxDispatch(clear_notification());
    if (hasUnread) {
      setOpenClearConfirmation(true);
    } else {
      performClear();
    }
  };

  const onConfirmClear = () => {
    performClear();
    setOpenClearConfirmation(false);
  };

  const handleDeleteNotification = (notification) => {
    reduxDispatch(delete_notification(notification));
    if (notification.type === "background_task") {
      dispatch(deleteTask(notification.id));
    } else {
      dispatch(delete_notification(notification.addedOn));
    }
  };

  const handleNotificationClick = (task) => {
    let navigateTo = null;
    let navigateState = {};

    if (task.type === "background_task" && task.task_category === "template") {
      navigateTo = `/customForms/templates/${task?.payload?.templateCode}`;
      navigateState = { state: { data: task?.payload, isGenerated: true } };

      if ((isRecording || isPaused) && !isProgressReady) {
        dispatch(set_show_popup(true, navigateTo, navigateState, task?.id));
        return;
      }
    } else if (
      task.type === "background_task" &&
      task.task_category === "soap"
    ) {
      let soapPatientId = task?.payload?.patientId;
      let soapEncounterId = task?.payload?.encounterId;

      if (!soapPatientId || !soapEncounterId) {
        const extracted = extractPatientAndEncounterIdFromComposition(
          task?.payload || {},
        );
        soapPatientId = extracted.soapPatientId;
        soapEncounterId = extracted.soapEncounterId;
      }
      const soapNoteTask = task?.taskGroups?.find(
        (item) => item.code === "task_group@notes.soapnote.generate",
      );

      navigateTo = `/patients/visits/${
        soapPatientId || task?.patientId
      }${`?tabId=${task?.status === "FAILED" ? 1 : 2}`}&visitId=${
        soapEncounterId || task?.encounterId
      }`;

      if ((isRecording || isPaused) && !isProgressReady) {
        dispatch(
          set_show_popup(true, navigateTo, {
            visitId: soapEncounterId || task?.encounterId,
            taskId: task?.id,
          }),
        );
        return;
      }

      dispatch(
        encounterObject({
          id: soapEncounterId || task?.encounterId,
          composition: { ...task?.payload },
        }),
      );
    }

    if (navigateTo) {
      navigate(navigateTo, navigateState);
      dispatch(markTaskAsRead(task.id));
      onClose();
    }
  };
  const NOTIFICATION_STATUS = {
    COMPLETED: "COMPLETED",
    FAILED: "FAILED",
    IN_PROGRESS: "IN_PROGRESS",
    QUEUED: "QUEUED",
  };

  const handleRetry = async (notification) => {
    const { id, task_category, patientId, encounterId, payload } = notification;

    try {
      if (task_category === "soap" && encounterId) {
        const newRetryTaskId = `${id}_retry_${Date.now()}`;
        dispatch(deleteTask(id));
        dispatch(
          addTask({
            id: newRetryTaskId,
            title: i18n.t("soap.initial"),
            progress: 10,
            status: NOTIFICATION_STATUS.IN_PROGRESS,
            practitionerId: currentPractitionerId,
            type: "background_task",
            task_category: task_category,
            addedOn: getUnixMs(),
            lastUpdated: Date.now(),
            patientName: notification.patientName,
            patientId: patientId,
            encounterId: encounterId,
            isRead: false,
          }),
        );

        const promptCode = "generate-encounter-soap-note";

        // Simulate progress updates for better UX
        let currentProgress = 10;
        const progressInterval = setInterval(() => {
          if (currentProgress < 90) {
            currentProgress = Math.min(currentProgress + 10, 90);
            dispatch(
              updateTask(newRetryTaskId, {
                progress: currentProgress,
                message: "Generating SOAP notes...",
                lastUpdated: Date.now(),
              }),
            );
          }
        }, 800);

        try {
          // Call the API
          const response = await GENERATE_REGENRATE_TEMPLATE(
            promptCode,
            encounterId,
            payload || {},
          );

          clearInterval(progressInterval);

          // Update to completed status with the response
          dispatch(
            updateTask(newRetryTaskId, {
              status: TASK_STATUS.COMPLETED,
              progress: 100,
              message: i18n.t("soap.completed"),
              payload: {
                ...response,
                patientId: patientId,
                encounterId: encounterId,
              },
              lastUpdated: Date.now(),
              isRead: false,
              error: null,
            }),
          );

          // Show success toast
          toast.success(i18n.t("soap.completed"), {
            data: {
              ignoreNotification: true,
            },
          });
        } catch (apiError) {
          clearInterval(progressInterval);
          dispatch(
            updateTask(newRetryTaskId, {
              status: TASK_STATUS.FAILED,
              progress: 0,
              message: "Retry failed. Please try again.",
              error: apiError?.response?.data?.message,
              lastUpdated: Date.now(),
            }),
          );

          throw apiError;
        }
      }
    } catch (error) {
      console.error("Failed to retry task:", error);
    }
  };

  return (
    <>
      <Confirmation
        open={openClearConfirmation}
        setOpen={setOpenClearConfirmation}
        message={i18n.t("notifications.UNREAD_CONFIRMATION")}
        callback={onConfirmClear}
      />
      <Dialog open={open} onClose={onClose}>
        <Box
          sx={{ position: "fixed", top: "64px", right: "2rem" }}
          className="notification-height"
        >
          <Paper
            sx={{
              width: "360px",
              minHeight: "200px",
              display: "flex",
              flexDirection: "column",
              borderRadius: "8px",
            }}
          >
            {/* Header with Unread Toggle and Clear All */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "12px 16px",
              }}
            >
              <Typography sx={{ fontWeight: 500, fontSize: "1.1rem" }}>
                {i18n.t("notifications.NOTIFICATIONS")}
              </Typography>

              <Box sx={{ display: "flex", alignItems: "center" }}>
                <FormControlLabel
                  labelPlacement="start"
                  control={
                    <Switch
                      size="small"
                      checked={showUnreadOnly}
                      onChange={() => setShowUnreadOnly((prev) => !prev)}
                      disabled={allNotifications.length === 0}
                    />
                  }
                  label={
                    <Typography sx={{ fontWeight: 500, fontSize: "12px" }}>
                      {i18n.t("notifications.UNREAD")}
                    </Typography>
                  }
                  sx={{
                    m: 0,
                    background: isDarkTheme ? "#2D2C5A" : "#F2F3FE",
                    borderRadius: "20px",
                    padding: "2px 6px 2px 8px",
                    justifyContent: "space-between",
                    border: "0.5px solid #c4c4c95c",
                  }}
                />
                <Tooltip title={i18n.t("common.CLEAR_ALL")}>
                  <IconButton
                    sx={{
                      height: "35px",
                      width: "35px",
                      ml: 1,
                    }}
                    onClick={handleClearAllNotifications}
                  >
                    <ClearAllRounded fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>

            <Divider
              sx={{ borderBottomWidth: "1px", borderColor: "text.secondary" }}
            />

            {/* Content Area */}
            <Box
              sx={{
                flex: 1,
                overflowY: "auto",
                overflowX: "hidden",
                maxHeight: "60vh",
                msOverflowStyle: "none",
                scrollbarWidth: "none",
                "&::-webkit-scrollbar": { display: "none" },
              }}
            >
              {/* Active Sync Group (Pinned) */}
              {activeSyncGroup && (
                <Box
                  sx={{
                    padding: "12px",
                    margin: "4px 12px 6px",
                    borderRadius: "8px",
                    background: "#5D5FEF14",
                    border: "0.5px solid #5D5FEF",
                    position: "sticky",
                    top: 0,
                    zIndex: 10,
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                      gap: 1,
                    }}
                  >
                    <Typography fontSize={13} sx={{ flex: 1 }}>
                      {activeSyncGroup.label || activeSyncGroup.code}
                    </Typography>
                  </Box>

                  <Box sx={{ mt: 1 }}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 1,
                      }}
                    >
                      <ProgressBar
                        progress={activeSyncGroup.progress || 0}
                        customSx={{ flex: 1 }}
                        showSteps={false}
                      />
                      <Chip
                        label={`${activeSyncGroup.progress || 0}%`}
                        size="small"
                        sx={{
                          fontSize: "12px",
                          height: "20px",
                          background: "transparent",
                          border: "none",
                        }}
                      />
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        mt: "4px",
                      }}
                    >
                      <UITooltip
                        title={
                          activeSyncGroup.status === "RUNNING"
                            ? (() => {
                                const sub = (activeSyncGroup.tasks || []).find(
                                  (tsk) => tsk && tsk.friendlySubtask,
                                );
                                const label = sub
                                  ? sub.friendlySubtask
                                  : activeSyncGroup.label;
                                return `Syncing: ${label}`;
                              })()
                            : activeSyncGroup.status === "COMPLETED"
                            ? "Completed"
                            : activeSyncGroup.status === "FAILED"
                            ? "Failed"
                            : "Pending"
                        }
                      >
                        <Typography
                          fontSize={11}
                          sx={{
                            fontFamily: "Poppins",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            pr: 1,
                          }}
                        >
                          {activeSyncGroup.status === "RUNNING"
                            ? (() => {
                                const sub = (activeSyncGroup.tasks || []).find(
                                  (tsk) => tsk && tsk.friendlySubtask,
                                );
                                const label = sub
                                  ? sub.friendlySubtask
                                  : activeSyncGroup.label;
                                return `Syncing: ${label}...`;
                              })()
                            : activeSyncGroup.status === "COMPLETED"
                            ? "Completed"
                            : activeSyncGroup.status === "FAILED"
                            ? "Failed"
                            : "Pending"}
                        </Typography>
                      </UITooltip>
                      <Typography
                        fontSize={9}
                        sx={{ fontWeight: "400", flexShrink: 0 }}
                      >
                        {formatNotificationTimestamp(
                          activeSyncGroup.addedOn || Date.now(),
                        )}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              )}

              {/* Completed Sync Groups - SOAP-style UI */}
              {completedSyncGroups?.length > 0 &&
                completedSyncGroups.map((group) => (
                  <React.Fragment key={group?.id || group?.addedOn}>
                    <Box
                      sx={{
                        padding: "12px",
                        margin: "4px 12px 6px",
                        borderRadius: "8px",
                        background: "#48BA7814",
                        border: "0.5px solid #48BA78",
                        transition: "opacity 0.3s ease-in-out",
                        opacity: group?.isRead ? 0.7 : 1,
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "flex-start",
                          gap: 1,
                        }}
                      >
                        <CheckCircleIcon
                          sx={{ color: "#4CAF50", fontSize: "20px" }}
                        />
                        <Typography fontSize={13} sx={{ flex: 1, ml: 1 }}>
                          Data Sync Completed.
                        </Typography>
                        <UITooltip title={i18n.t("common.DELETE")}>
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteNotification(group?.addedOn);
                            }}
                            sx={{ mt: "-4px" }}
                          >
                            <ClearIcon fontSize="small" />
                          </IconButton>
                        </UITooltip>
                      </Box>

                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          mt: 1,
                          ml: 4.5,
                        }}
                      >
                        <Typography
                          fontSize={11}
                          sx={{ fontWeight: "400", flex: 1 }}
                        >
                          {/* Additional info can go here */}
                        </Typography>
                        <Typography
                          fontSize={9}
                          sx={{ fontWeight: "400", flexShrink: 0 }}
                        >
                          {formatNotificationTimestamp(group?.addedOn)}
                        </Typography>
                      </Box>
                    </Box>
                    <Divider variant="middle" />
                  </React.Fragment>
                ))}

              {/* Grouped Notifications (from Document 1) */}
              {groupKeys.length > 0 ? (
                groupKeys.map((group) => (
                  <Box key={group} sx={{ mt: 1, mb: 2 }}>
                    <Typography fontSize={"0.8rem"} sx={{ padding: "0 14px" }}>
                      {group}
                    </Typography>
                    {groupedNotifications[group].map((notification) => (
                      <NotificationItem
                        key={notification.id || notification.addedOn}
                        notification={notification}
                        onDelete={handleDeleteNotification}
                        isDarkTheme={isDarkTheme}
                        language={i18n.language}
                        handleNotificationClick={handleNotificationClick}
                        onRetry={handleRetry}
                      />
                    ))}
                  </Box>
                ))
              ) : otherNotifications?.length > 0 ? (
                /* Other Notifications (from Document 2) */
                otherNotifications.map((notification) => (
                  <React.Fragment
                    key={
                      notification?.addedOn ||
                      notification?.id ||
                      notification?.message
                    }
                  >
                    <Box
                      sx={{
                        padding: "8px 10px",
                        margin: "4px",
                        background: !notification?.isRead
                          ? app?.theme === "light"
                            ? "#dfe9f5"
                            : app?.theme === "dark"
                            ? "#5c5d5e"
                            : "inherit"
                          : "inherit",
                        borderRadius: "5px",
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          mb: 0,
                        }}
                      >
                        <Typography
                          fontSize={14}
                          sx={{ fontFamily: "Poppins", fontWeight: "500" }}
                        >
                          {notification?.message}
                          <Typography
                            fontSize={10}
                            sx={{
                              fontFamily: "Poppins",
                              fontWeight: "400",
                            }}
                          >
                            {dayjs(
                              epochConverter(notification?.addedOn),
                            ).format(
                              mappedLanguages?.[i18n?.language]
                                ?.timeWithDateFormat,
                            )}
                          </Typography>
                        </Typography>
                        <IconButton
                          onClick={() =>
                            handleDeleteNotification(notification?.addedOn)
                          }
                        >
                          <ClearIcon />
                        </IconButton>
                      </Box>
                    </Box>
                    <Divider variant="middle" />
                  </React.Fragment>
                ))
              ) : !activeSyncGroup && !completedSyncGroups?.length ? (
                <DialogContent
                  sx={{
                    fontFamily: "Poppins",
                    textAlign: "center",
                    fontWeight: 500,
                    mt: 3,
                  }}
                >
                  {i18n.t("notifications.NO_NOTIFICATIONS_RIGHT_NOW")}
                </DialogContent>
              ) : null}
            </Box>

            <DialogActions>
              <Button onClick={onClose} color="primary">
                {i18n.t("notifications.CLOSE")}
              </Button>
            </DialogActions>
          </Paper>
        </Box>
      </Dialog>
    </>
  );
};

export default NotificationModal;
